import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import { isRetiredBriefSectionId } from "@/shared/brief/default-sequence";

const HOME_CAROUSEL_KEY = "tb_public_web_home_carousel_v1";
const HOME_CAROUSEL_EVENT = "tb-public-web-home-carousel-changed";

const LAUNCH_COUNTRIES = ["US", "CA", "AU", "GB", "DE", "FR", "IN", "JP"] as const;
type LaunchCountry = (typeof LAUNCH_COUNTRIES)[number];

export const HOME_CAROUSEL_CARD_IDS = {
  dailyBrief: "daily_brief",
  quote: "quote",
  spark: "spark",
  funFacts: "fun_facts",
  worldFacts: "world_facts",
  spaceFacts: "space_facts",
  dailyMastery: "daily_mastery",
  markets: "markets",
  watchlist: "watchlist",
  todayIdea: "today_idea",
  commute: "commute",
  calendar: "calendar",
  weather: "weather",
  cricket: "cricket",
  tasks: "tasks",
  newsLocal: "news_local",
} as const;

type CoreHomeCarouselCardId = (typeof HOME_CAROUSEL_CARD_IDS)[keyof typeof HOME_CAROUSEL_CARD_IDS];

export type HomeCarouselCardId =
  | CoreHomeCarouselCardId
  | `news_${string}`
  | `sports_${string}`
  | `weather_custom_${string}`;

export type HomeCarouselCardDefinition = {
  id: HomeCarouselCardId;
  label: string;
  eyebrow: string;
  premium?: boolean;
};

export type HomeCarouselSettings = {
  order: HomeCarouselCardId[];
  hidden: Record<HomeCarouselCardId, boolean>;
};

type CountryCarouselConfig = {
  name: string;
  nationalNewsId: `news_${string}`;
  nationalNewsLabel: string;
  defaultCards: HomeCarouselCardId[];
  sports: Array<{ id: `sports_${string}`; label: string }>;
  cricketCard: boolean;
};

/**
 * Exact Android `NewsSources.categoryEntriesForMarketCarousel` contract.
 *
 * Keep this explicit. Android intentionally has no Germany Politics card and
 * Japan exposes only World, AI, and Technology. `news_local` is absent for all
 * launch markets because every current `CountryConfig` hides the local section.
 */
export const ANDROID_HOME_NEWS_CATEGORY_IDS_BY_COUNTRY = {
  US: [
    "news_global_us_national",
    "news_global_us_world",
    "news_global_us_business",
    "news_global_us_ai",
    "news_global_us_technology",
    "news_global_us_sports",
    "news_global_us_politics",
  ],
  CA: [
    "news_global_ca_national",
    "news_global_ca_world",
    "news_global_ca_business",
    "news_global_ca_ai",
    "news_global_ca_technology",
    "news_global_ca_sports",
    "news_global_ca_politics",
  ],
  AU: [
    "news_global_au_national",
    "news_global_au_world",
    "news_global_au_business",
    "news_global_au_ai",
    "news_global_au_technology",
    "news_global_au_sports",
    "news_global_au_politics",
  ],
  GB: [
    "news_global_gb_national",
    "news_global_gb_world",
    "news_global_gb_business",
    "news_global_gb_ai",
    "news_global_gb_technology",
    "news_global_gb_sports",
    "news_global_gb_politics",
  ],
  DE: [
    "news_global_de_national",
    "news_global_de_world",
    "news_global_de_business",
    "news_global_de_ai",
    "news_global_de_technology",
    "news_global_de_sports",
  ],
  FR: [
    "news_global_fr_national",
    "news_global_fr_world",
    "news_global_fr_business",
    "news_global_fr_ai",
    "news_global_fr_technology",
    "news_global_fr_sports",
    "news_global_fr_politics",
  ],
  IN: [
    "news_global_in_national",
    "news_global_in_world",
    "news_global_in_business",
    "news_global_in_ai",
    "news_global_in_technology",
    "news_global_in_sports",
    "news_global_in_politics",
  ],
  JP: ["news_global_jp_world", "news_global_jp_ai", "news_global_jp_technology"],
} as const satisfies Record<LaunchCountry, readonly `news_${string}`[]>;

/**
 * Mirrors Android `HomeCarouselCards.buildDefaultVisibleOrder`: regional news
 * first, then live context, discovery, AI, the market's primary sport, and the
 * productivity/local/learning tail. `daily_brief` is prepended separately.
 */
function androidFreshDeck(
  newsCards: HomeCarouselCardId[],
  aiNewsCard: HomeCarouselCardId,
  primarySportCard: HomeCarouselCardId,
): HomeCarouselCardId[] {
  return [
    ...newsCards,
    "markets",
    "watchlist",
    "quote",
    "spark",
    "fun_facts",
    "world_facts",
    "space_facts",
    aiNewsCard,
    primarySportCard,
    "tasks",
    "calendar",
    "weather",
    "commute",
    "daily_mastery",
    "today_idea",
  ];
}

const COUNTRY_CONFIG: Record<LaunchCountry, CountryCarouselConfig> = {
  US: {
    name: "United States",
    nationalNewsId: "news_global_us_national",
    nationalNewsLabel: "US News",
    defaultCards: androidFreshDeck(
      ["news_global_us_national", "news_global_us_world", "news_global_us_technology"],
      "news_global_us_ai",
      "sports_nfl",
    ),
    sports: [
      { id: "sports_nfl", label: "NFL" },
      { id: "sports_mlb", label: "MLB" },
      { id: "sports_nba", label: "NBA" },
      { id: "sports_football", label: "Soccer" },
    ],
    cricketCard: false,
  },
  CA: {
    name: "Canada",
    nationalNewsId: "news_global_ca_national",
    nationalNewsLabel: "Canada News",
    defaultCards: androidFreshDeck(
      ["news_global_ca_national", "news_global_ca_world", "news_global_ca_technology"],
      "news_global_ca_ai",
      "sports_hockey",
    ),
    sports: [
      { id: "sports_hockey", label: "NHL" },
      { id: "sports_nba", label: "NBA" },
      { id: "sports_football", label: "Soccer" },
    ],
    cricketCard: false,
  },
  AU: {
    name: "Australia",
    nationalNewsId: "news_global_au_national",
    nationalNewsLabel: "Australia News",
    defaultCards: androidFreshDeck(
      ["news_global_au_national", "news_global_au_world", "news_global_au_technology"],
      "news_global_au_ai",
      "sports_afl",
    ),
    sports: [
      { id: "sports_afl", label: "AFL" },
      { id: "sports_football", label: "Soccer" },
    ],
    // Android's add-card catalog exposes the dedicated legacy `cricket`
    // definition for AU, never a duplicate modern picker row.
    cricketCard: true,
  },
  GB: {
    name: "United Kingdom",
    nationalNewsId: "news_global_gb_national",
    nationalNewsLabel: "UK News",
    defaultCards: androidFreshDeck(
      ["news_global_gb_national", "news_global_gb_world", "news_global_gb_technology"],
      "news_global_gb_ai",
      "sports_football",
    ),
    sports: [
      { id: "sports_football", label: "Soccer" },
      { id: "sports_cricket", label: "Cricket" },
    ],
    cricketCard: false,
  },
  DE: {
    name: "Germany",
    nationalNewsId: "news_global_de_national",
    nationalNewsLabel: "Germany News",
    defaultCards: androidFreshDeck(
      ["news_global_de_national", "news_global_de_world", "news_global_de_technology"],
      "news_global_de_ai",
      "sports_football",
    ),
    sports: [{ id: "sports_football", label: "Soccer" }],
    cricketCard: false,
  },
  FR: {
    name: "France",
    nationalNewsId: "news_global_fr_national",
    nationalNewsLabel: "France News",
    defaultCards: androidFreshDeck(
      ["news_global_fr_national", "news_global_fr_world", "news_global_fr_technology"],
      "news_global_fr_ai",
      "sports_football",
    ),
    sports: [{ id: "sports_football", label: "Soccer" }],
    cricketCard: false,
  },
  IN: {
    name: "India",
    nationalNewsId: "news_global_in_national",
    nationalNewsLabel: "India News",
    defaultCards: androidFreshDeck(
      ["news_global_in_national", "news_global_in_world", "news_global_in_technology"],
      "news_global_in_ai",
      "cricket",
    ),
    sports: [{ id: "sports_football", label: "Soccer" }],
    cricketCard: true,
  },
  JP: {
    name: "Japan",
    nationalNewsId: "news_global_jp_world",
    nationalNewsLabel: "Japan News",
    defaultCards: androidFreshDeck(
      ["news_global_jp_world", "news_global_jp_technology"],
      "news_global_jp_ai",
      "sports_baseball",
    ),
    sports: [
      { id: "sports_baseball", label: "Baseball" },
      { id: "sports_football", label: "Soccer" },
    ],
    cricketCard: false,
  },
};

const CORE_DEFINITIONS: HomeCarouselCardDefinition[] = [
  { id: "daily_brief", label: "Top 3 Things", eyebrow: "Today's Brief" },
  { id: "quote", label: "Quote", eyebrow: "Reflection" },
  { id: "spark", label: "Spark", eyebrow: "Today's Spark" },
  { id: "daily_mastery", label: "Daily Mastery", eyebrow: "Learn", premium: true },
  { id: "fun_facts", label: "Fun Facts", eyebrow: "Discovery" },
  { id: "world_facts", label: "World Facts", eyebrow: "Context" },
  { id: "space_facts", label: "Space Facts", eyebrow: "Science" },
  { id: "markets", label: "Markets", eyebrow: "Market pulse" },
  { id: "watchlist", label: "Your Watchlist", eyebrow: "Watchlist movers" },
  { id: "today_idea", label: "Today's Idea", eyebrow: "Create", premium: true },
  { id: "commute", label: "Commute", eyebrow: "Local" },
  { id: "calendar", label: "Calendar", eyebrow: "My day" },
  { id: "weather", label: "Weather", eyebrow: "Local" },
  { id: "cricket", label: "Cricket", eyebrow: "Sports" },
  { id: "tasks", label: "Tasks", eyebrow: "Planner" },
];

function normalizeCountry(country: string | undefined | null): LaunchCountry {
  const value = String(country || "US")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  return (LAUNCH_COUNTRIES as readonly string[]).includes(value) ? (value as LaunchCountry) : "US";
}

function distinctIds(ids: HomeCarouselCardId[]): HomeCarouselCardId[] {
  return [...new Set(ids)];
}

/**
 * Android treats Tasks and Calendar as one productivity module. Calendar
 * follows Tasks after defaults, persisted customization, and server hints.
 */
function ensureTasksCalendarAdjacent(ids: HomeCarouselCardId[]): HomeCarouselCardId[] {
  const deduped = distinctIds(ids);
  const taskIndex = deduped.indexOf("tasks");
  if (taskIndex < 0 || !deduped.includes("calendar")) return deduped;
  const withoutCalendar: HomeCarouselCardId[] = deduped.filter((id) => id !== "calendar");
  withoutCalendar.splice(withoutCalendar.indexOf("tasks") + 1, 0, "calendar");
  return withoutCalendar;
}

function canonicalHomeCarouselOrder(ids: HomeCarouselCardId[]): HomeCarouselCardId[] {
  const adjacent = ensureTasksCalendarAdjacent(ids);
  return adjacent.includes("daily_brief")
    ? ["daily_brief", ...adjacent.filter((id) => id !== "daily_brief")]
    : adjacent;
}

function newsDefinition(
  id: HomeCarouselCardId,
  label: string,
  eyebrow = "Headlines",
): HomeCarouselCardDefinition {
  return { id, label, eyebrow };
}

function androidNewsDefinition(
  id: `news_${string}`,
  config: CountryCarouselConfig,
): HomeCarouselCardDefinition {
  const category = id.slice(id.lastIndexOf("_") + 1);
  switch (category) {
    case "national":
      return newsDefinition(id, config.nationalNewsLabel);
    case "world":
      return newsDefinition(id, "World", "Context");
    case "business":
      return newsDefinition(id, "Biz · Global", "Business");
    case "ai":
      return newsDefinition(id, "AI News", "Technology");
    case "technology":
      return newsDefinition(id, "Tech", "Technology");
    case "sports":
      return newsDefinition(id, "Sports", "Sports");
    case "politics":
      return newsDefinition(id, "Politics", "Politics");
    default:
      return newsDefinition(id, "News");
  }
}

export function homeCarouselDefinitionsForCountry(country = "US"): HomeCarouselCardDefinition[] {
  const cc = normalizeCountry(country);
  const config = COUNTRY_CONFIG[cc];
  const categoryDefinitions = ANDROID_HOME_NEWS_CATEGORY_IDS_BY_COUNTRY[cc].map((id) =>
    androidNewsDefinition(id, config),
  );
  const sportsDefinitions = config.sports.map<HomeCarouselCardDefinition>((sport) => ({
    id: sport.id,
    label: sport.label,
    eyebrow: "Sports",
  }));
  const allowedCore = CORE_DEFINITIONS.filter(
    (definition) => definition.id !== "cricket" || config.cricketCard,
  );
  return [...allowedCore, ...categoryDefinitions, ...sportsDefinitions].filter(
    (definition, index, all) =>
      all.findIndex((candidate) => candidate.id === definition.id) === index,
  );
}

export function defaultHomeCarouselOrderForCountry(country = "US"): HomeCarouselCardId[] {
  const config = COUNTRY_CONFIG[normalizeCountry(country)];
  return canonicalHomeCarouselOrder(["daily_brief", ...config.defaultCards]);
}

export const HOME_CAROUSEL_DEFINITIONS = homeCarouselDefinitionsForCountry("US");
export const DEFAULT_HOME_CAROUSEL_ORDER = defaultHomeCarouselOrderForCountry("US");

function customWeatherId(id: string): id is `weather_custom_${string}` {
  return /^weather_custom_[a-z0-9_-]+$/i.test(id);
}

function migrateLegacyId(value: unknown, country: string): HomeCarouselCardId | null {
  const id = String(value || "")
    .trim()
    .toLowerCase();
  if (!id) return null;
  const cc = normalizeCountry(country);
  const config = COUNTRY_CONFIG[cc];
  const regional = cc.toLowerCase();
  const legacy: Record<string, HomeCarouselCardId | null> = {
    news: config.nationalNewsId,
    news_india: "news_global_in_national",
    news_us_national: "news_global_us_national",
    world: `news_global_${regional}_world`,
    ai_news: `news_global_${regional}_ai`,
    sports: config.sports[0]?.id || "sports_football",
    saved: null,
    premium_morning_intelligence: "daily_brief",
    premium_market_intelligence: "markets",
    premium_watchlist_insights: "watchlist",
    premium_weekend_digest: null,
  };
  if (Object.prototype.hasOwnProperty.call(legacy, id)) return legacy[id];
  if (id === "sports_cricket" && config.cricketCard) return "cricket";
  const regionalNews = id.match(/^news_global_[a-z]{2}_(.+)$/);
  if (regionalNews) {
    const category = cc === "JP" && regionalNews[1] === "national" ? "world" : regionalNews[1];
    return `news_global_${regional}_${category}`;
  }
  if (id.startsWith("sports_") && !config.sports.some((sport) => sport.id === id)) {
    return config.sports[0]?.id || "sports_football";
  }
  return id as HomeCarouselCardId;
}

function isKnownForCountry(id: HomeCarouselCardId, country: string): boolean {
  return (
    customWeatherId(id) ||
    homeCarouselDefinitionsForCountry(country).some((definition) => definition.id === id)
  );
}

const LEGACY_MARKET_BRIEF_SECTION_IDS = new Set([
  "market",
  "market_indexes",
  "market_commodities",
  "market_energy_crypto",
]);

function normalizedBriefSectionIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const value of raw) {
    const id = String(value || "")
      .trim()
      .toLowerCase();
    if (!id || id.length > 80 || isRetiredBriefSectionId(id)) continue;
    const expanded = LEGACY_MARKET_BRIEF_SECTION_IDS.has(id) ? "market_pulse" : id;
    if (seen.has(expanded)) continue;
    seen.add(expanded);
    ids.push(expanded);
  }
  return ids;
}

/** Mirrors Android `TodaysBriefJourneySectionResolver.carouselIdForBriefSectionId`. */
export function homeCarouselIdForBriefSection(
  sectionId: unknown,
  country = "US",
): HomeCarouselCardId | null {
  const id = String(sectionId || "")
    .trim()
    .toLowerCase();
  if (!id) return null;
  let candidate: string | null;
  switch (id) {
    case "quote":
    case "spark":
    case "weather":
    case "commute":
    case "watchlist":
    case "cricket":
    case "daily_mastery":
    case "today_idea":
    case "fun_facts":
      candidate = id;
      break;
    case "market":
    case "market_pulse":
    case "market_indexes":
    case "market_commodities":
    case "market_energy_crypto":
      candidate = "markets";
      break;
    case "todos":
      candidate = "tasks";
      break;
    case "my_day":
      candidate = "calendar";
      break;
    case "world_affairs":
      candidate = "world_facts";
      break;
    case "space":
      candidate = "space_facts";
      break;
    case "news":
      candidate = COUNTRY_CONFIG[normalizeCountry(country)].nationalNewsId;
      break;
    default:
      if (customWeatherId(id) || id.startsWith("news_")) {
        candidate = id;
      } else if (id.startsWith("sport_")) {
        candidate = `sports_${id.slice("sport_".length)}`;
      } else {
        candidate = null;
      }
  }
  const migrated = candidate ? migrateLegacyId(candidate, country) : null;
  return migrated && isKnownForCountry(migrated, country) ? migrated : null;
}

/** Mirrors Android `TodaysBriefJourneySectionResolver.briefSectionIdsForCarouselId`. */
export function briefSectionIdsForHomeCarouselId(
  rawId: HomeCarouselCardId,
  country = "US",
): string[] {
  const id = migrateLegacyId(rawId, country);
  if (!id || !isKnownForCountry(id, country)) return [];
  switch (id) {
    case "daily_brief":
      return [];
    case "quote":
    case "spark":
    case "weather":
    case "commute":
    case "watchlist":
    case "cricket":
    case "daily_mastery":
    case "today_idea":
    case "fun_facts":
      return [id];
    case "markets":
      return ["market_pulse"];
    case "tasks":
      return ["todos"];
    case "calendar":
      return ["my_day"];
    case "world_facts":
      return ["world_affairs"];
    case "space_facts":
      return ["space"];
    default:
      if (customWeatherId(id) || id.startsWith("news_")) return [id];
      if (id.startsWith("sports_")) return [`sport_${id.slice("sports_".length)}`];
      return [];
  }
}

export function removableBriefSectionIdsForHomeCarouselId(
  id: HomeCarouselCardId,
  country = "US",
): string[] {
  const sectionIds = briefSectionIdsForHomeCarouselId(id, country);
  const normalizedId = migrateLegacyId(id, country);
  if (normalizedId && normalizedId === COUNTRY_CONFIG[normalizeCountry(country)].nationalNewsId) {
    sectionIds.push("news");
  }
  if (normalizedId === "markets") {
    sectionIds.push("market", "market_indexes", "market_commodities", "market_energy_crypto");
  }
  return [...new Set(sectionIds)];
}

export function homeCarouselIdsForBriefSections(
  sectionIds: unknown,
  disabledSectionIds: unknown = [],
  country = "US",
): HomeCarouselCardId[] {
  const disabled = new Set(normalizedBriefSectionIds(disabledSectionIds));
  return distinctIds(
    normalizedBriefSectionIds(sectionIds)
      .filter((id) => !disabled.has(id))
      .map((id) => homeCarouselIdForBriefSection(id, country))
      .filter((id): id is HomeCarouselCardId => Boolean(id)),
  );
}

function isBriefControlledHomeCarouselId(id: HomeCarouselCardId, country: string): boolean {
  return briefSectionIdsForHomeCarouselId(id, country).length > 0;
}

/**
 * Android projects the active Brief profile into Home and keeps only explicit
 * Home-only extras. Custom-city weather is retained defensively because older
 * synced web snapshots may predate its Brief-profile persistence.
 */
export function projectActiveBriefHomeCarouselIds(options: {
  sectionIds: unknown;
  disabledSectionIds?: unknown;
  settings: HomeCarouselSettings;
  country?: string;
}): HomeCarouselCardId[] {
  const country = options.country || "US";
  const fromBrief = homeCarouselIdsForBriefSections(
    options.sectionIds,
    options.disabledSectionIds,
    country,
  );
  const explicitExtras = canonicalVisibleHomeCarouselIds(options.settings).filter(
    (id) => customWeatherId(id) || !isBriefControlledHomeCarouselId(id, country),
  );
  return canonicalHomeCarouselOrder([...fromBrief, ...explicitExtras]);
}

function expandProjectedProductivityPair(
  projectedVisible: HomeCarouselCardId[],
): HomeCarouselCardId[] {
  const hasTasks = projectedVisible.includes("tasks");
  const hasCalendar = projectedVisible.includes("calendar");
  if (hasTasks === hasCalendar) return projectedVisible;
  const visibleAnchor = hasTasks ? "tasks" : "calendar";
  return projectedVisible.flatMap<HomeCarouselCardId>((id) =>
    id === visibleAnchor ? ["tasks", "calendar"] : [id],
  );
}

/**
 * Presents the whole addable catalog while marking only the profile projection
 * as visible. This is view state; callers must not persist it implicitly.
 */
export function homeCarouselSettingsForProjectedVisibleIds(
  settings: HomeCarouselSettings,
  visibleIds: HomeCarouselCardId[],
  country = "US",
): HomeCarouselSettings {
  const normalized = normalizeHomeCarouselSettings(settings, country);
  const projectedVisible = distinctIds(
    visibleIds
      .map((id) => migrateLegacyId(id, country))
      .filter((id): id is HomeCarouselCardId => Boolean(id && isKnownForCountry(id, country))),
  );
  const visible = new Set(projectedVisible);
  const projectedOrder = expandProjectedProductivityPair(projectedVisible);
  const placed = new Set(projectedOrder);
  const order = canonicalHomeCarouselOrder([
    ...projectedOrder,
    ...normalized.order.filter((id) => !placed.has(id)),
  ]);
  return {
    order,
    hidden: Object.fromEntries(order.map((id) => [id, !visible.has(id)])) as Record<
      HomeCarouselCardId,
      boolean
    >,
  };
}

function briefSectionControlsHomeCarouselId(
  sectionId: string,
  id: HomeCarouselCardId,
  country: string,
): boolean {
  return homeCarouselIdForBriefSection(sectionId, country) === migrateLegacyId(id, country);
}

export function setBriefSectionSelectedForHomeCarousel(
  sectionIds: unknown,
  disabledSectionIds: unknown,
  id: HomeCarouselCardId,
  selected: boolean,
  country = "US",
): { sectionIds: string[]; disabledSectionIds: string[] } {
  const current = Array.isArray(sectionIds)
    ? sectionIds
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase(),
        )
        .filter((value) => value.length > 0 && value.length <= 80)
    : [];
  const disabled = Array.isArray(disabledSectionIds)
    ? disabledSectionIds
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase(),
        )
        .filter((value) => value.length > 0 && value.length <= 80)
    : [];
  const additions = briefSectionIdsForHomeCarouselId(id, country);
  if (!additions.length) return { sectionIds: current, disabledSectionIds: disabled };
  const controlsCard = (sectionId: string) =>
    briefSectionControlsHomeCarouselId(sectionId, id, country);
  const enabledDisabled = disabled.filter((sectionId) => !controlsCard(sectionId));
  if (selected) {
    const alreadyPresent = current.some(controlsCard);
    return {
      sectionIds: alreadyPresent ? current : [...current, additions[0]],
      disabledSectionIds: enabledDisabled,
    };
  }
  const remaining = current.filter((sectionId) => !controlsCard(sectionId));
  if (remaining.length || !current.some(controlsCard)) {
    return { sectionIds: remaining, disabledSectionIds: enabledDisabled };
  }
  // `normalizeBriefProfile` treats an empty list as "restore defaults". Keep the
  // final token disabled so hiding the last card cannot resurrect the full deck.
  return {
    sectionIds: current,
    disabledSectionIds: [...new Set([...enabledDisabled, ...current.filter(controlsCard)])],
  };
}

/**
 * Reorders only Brief sections represented by carousel cards, leaving Brief-only
 * modules (for example productivity or finance) in their original positions.
 */
export function mergeHomeCarouselOrderIntoBriefSections(
  sectionIds: unknown,
  orderedIds: HomeCarouselCardId[],
  country = "US",
): string[] {
  const current = Array.isArray(sectionIds)
    ? sectionIds
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase(),
        )
        .filter((value) => value.length > 0 && value.length <= 80)
    : [];
  const groups = new Map<HomeCarouselCardId, string[]>();
  for (const sectionId of current) {
    const id = homeCarouselIdForBriefSection(sectionId, country);
    if (!id) continue;
    groups.set(id, [...(groups.get(id) || []), sectionId]);
  }
  const orderedSections = distinctIds(
    orderedIds
      .map((id) => migrateLegacyId(id, country))
      .filter((id): id is HomeCarouselCardId => Boolean(id && groups.has(id))),
  ).flatMap((id) => groups.get(id) || []);
  const replacements = orderedSections[Symbol.iterator]();
  return current.map((sectionId) =>
    homeCarouselIdForBriefSection(sectionId, country)
      ? replacements.next().value || sectionId
      : sectionId,
  );
}

/**
 * Projects a Brief profile onto the exact visible Home deck. Card-backed
 * sections are filtered and reordered from Home; Brief-only modules retain
 * their original slots and order.
 */
export function mergeVisibleHomeCarouselOrderIntoBriefSections(
  sectionIds: unknown,
  visibleIds: HomeCarouselCardId[],
  country = "US",
): string[] {
  const visible = new Set(
    visibleIds
      .map((id) => migrateLegacyId(id, country))
      .filter((id): id is HomeCarouselCardId => Boolean(id)),
  );
  const retained = normalizedBriefSectionIds(sectionIds).filter((sectionId) => {
    const cardId = homeCarouselIdForBriefSection(sectionId, country);
    return !cardId || visible.has(cardId);
  });
  return mergeHomeCarouselOrderIntoBriefSections(retained, visibleIds, country);
}

export function homeCarouselDefinitionFor(
  id: HomeCarouselCardId,
  country = "US",
): HomeCarouselCardDefinition {
  if (customWeatherId(id)) {
    return { id, label: "Weather - Custom City", eyebrow: "Local" };
  }
  return (
    homeCarouselDefinitionsForCountry(country).find((definition) => definition.id === id) || {
      id,
      label: "Brief card",
      eyebrow: "Today's Brief",
    }
  );
}

function sourceObject(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

function catalogOrder(country: string, raw?: Record<string, unknown> | null): HomeCarouselCardId[] {
  const defaults = defaultHomeCarouselOrderForCountry(country);
  const definitions = homeCarouselDefinitionsForCountry(country).map((definition) => definition.id);
  const custom = Array.isArray(raw?.order)
    ? raw.order
        .map((value) => migrateLegacyId(value, country))
        .filter((id): id is HomeCarouselCardId => Boolean(id && customWeatherId(id)))
    : [];
  return distinctIds([...defaults, ...definitions, ...custom]);
}

export function normalizeHomeCarouselOrder(raw: unknown, country = "US"): HomeCarouselCardId[] {
  const source = sourceObject(raw);
  const known = catalogOrder(country, source);
  const knownSet = new Set(known);
  const seen = new Set<HomeCarouselCardId>();
  const explicit = Array.isArray(source?.order) ? source.order : Array.isArray(raw) ? raw : [];
  const ordered: HomeCarouselCardId[] = [];
  for (const value of explicit) {
    const id = migrateLegacyId(value, country);
    if (!id || (!knownSet.has(id) && !customWeatherId(id)) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  const completed = [...ordered, ...known.filter((id) => !seen.has(id))];
  return canonicalHomeCarouselOrder(completed);
}

export function normalizeHomeCarouselHidden(
  raw: unknown,
  country = "US",
): Record<HomeCarouselCardId, boolean> {
  return normalizeHomeCarouselSettings(raw, country).hidden;
}

export function normalizeHomeCarouselSettings(raw: unknown, country = "US"): HomeCarouselSettings {
  const source = sourceObject(raw);
  const order = normalizeHomeCarouselOrder(source || raw, country);
  const defaults = new Set(defaultHomeCarouselOrderForCountry(country));
  const hiddenSource = sourceObject(source?.hidden) || {};
  const explicitIds = new Set<HomeCarouselCardId>();
  const visibleByMigratedId = new Map<HomeCarouselCardId, boolean>();

  if (source && Array.isArray(source.order)) {
    for (const value of source.order) {
      const id = migrateLegacyId(value, country);
      if (!id || !isKnownForCountry(id, country)) continue;
      explicitIds.add(id);
      const rawId = String(value || "").trim();
      const visible = hiddenSource[rawId] !== true;
      visibleByMigratedId.set(id, visibleByMigratedId.get(id) || false || visible);
    }
  }

  const hidden = order.reduce(
    (result, id) => {
      if (!source) {
        result[id] = !defaults.has(id);
      } else if (explicitIds.has(id)) {
        result[id] = visibleByMigratedId.get(id) !== true;
      } else {
        result[id] = true;
      }
      return result;
    },
    {} as Record<HomeCarouselCardId, boolean>,
  );
  return { order, hidden };
}

function readStoredSettings(): unknown {
  try {
    const raw = readAccountScopedStorage(HOME_CAROUSEL_KEY);
    return raw == null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

export function loadHomeCarouselSettings(country = "US"): HomeCarouselSettings {
  return normalizeHomeCarouselSettings(readStoredSettings(), country);
}

export function saveHomeCarouselSettings(
  settings: HomeCarouselSettings,
  country = "US",
): HomeCarouselSettings {
  const next = normalizeHomeCarouselSettings(settings, country);
  try {
    writeAccountScopedStorage(HOME_CAROUSEL_KEY, JSON.stringify(next));
  } catch {
    /* carousel customization is best effort */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(HOME_CAROUSEL_EVENT, { detail: next }));
  }
  return next;
}

export function migrateExplicitHomeCarouselDeckForCountry(
  previousCountry: string,
  targetCountry: string,
): HomeCarouselSettings | null {
  const raw = readStoredSettings();
  if (raw == null) return null;
  const previous = normalizeHomeCarouselSettings(raw, previousCountry);
  const visible = canonicalVisibleHomeCarouselIds(previous);
  const hasNews = visible.some((id) => id === "news_local" || id.startsWith("news_"));
  const targetNews = defaultHomeCarouselOrderForCountry(targetCountry).filter(
    (id) => id !== "news_local" && id.startsWith("news_"),
  );
  const withoutNews = visible.filter((id) => id !== "news_local" && !id.startsWith("news_"));
  const migratedVisible = hasNews ? [...targetNews, ...withoutNews] : withoutNews;
  const migrated = homeCarouselSettingsFromVisibleIds(migratedVisible, targetCountry);
  return migrated ? saveHomeCarouselSettings(migrated, targetCountry) : null;
}

export function homeCarouselSettingsFromVisibleIds(
  raw: unknown,
  country = "US",
): HomeCarouselSettings | null {
  const values = typeof raw === "string" ? raw.split(",") : Array.isArray(raw) ? raw : null;
  if (!values) return null;
  const migrated = values
    .map((value) => migrateLegacyId(value, country))
    .filter((id): id is HomeCarouselCardId => Boolean(id && isKnownForCountry(id, country)));
  return normalizeHomeCarouselSettings(
    {
      order: migrated,
      hidden: Object.fromEntries(migrated.map((id) => [id, false])),
    },
    country,
  );
}

export function canonicalVisibleHomeCarouselIds(
  settings: HomeCarouselSettings,
): HomeCarouselCardId[] {
  return settings.order.filter((id) => settings.hidden[id] !== true);
}

export function visibleHomeCarouselIds(
  settings: HomeCarouselSettings,
  _country = "US",
): HomeCarouselCardId[] {
  return canonicalHomeCarouselOrder(canonicalVisibleHomeCarouselIds(settings));
}

export function mergeHomeCarouselWithServerHints(
  userVisibleOrdered: HomeCarouselCardId[],
  serverHintOrdered: unknown,
  country = "US",
): HomeCarouselCardId[] {
  const hints = Array.isArray(serverHintOrdered) ? serverHintOrdered : [];
  if (!hints.length) return canonicalHomeCarouselOrder([...userVisibleOrdered]);
  const seen = new Set<HomeCarouselCardId>();
  const normalizedHints = hints
    .map((value) => migrateLegacyId(value, country))
    .filter((id): id is HomeCarouselCardId =>
      Boolean(id && isKnownForCountry(id, country) && !seen.has(id) && seen.add(id)),
    );
  const userSet = new Set(userVisibleOrdered);
  const fromServer = normalizedHints.filter((id) => userSet.has(id));
  const serverSet = new Set(fromServer);
  const merged = [...fromServer, ...userVisibleOrdered.filter((id) => !serverSet.has(id))];
  return canonicalHomeCarouselOrder(merged);
}

export function resolveVisibleHomeCarouselIds(
  options: {
    settings?: HomeCarouselSettings;
    serverHints?: unknown;
    country?: string;
    briefSectionIds?: unknown;
    disabledBriefSectionIds?: unknown;
  } = {},
): HomeCarouselCardId[] {
  const country = options.country || "US";
  const settings = options.settings || loadHomeCarouselSettings(country);
  const userVisibleOrdered =
    options.briefSectionIds === undefined
      ? visibleHomeCarouselIds(settings, country)
      : projectActiveBriefHomeCarouselIds({
          sectionIds: options.briefSectionIds,
          disabledSectionIds: options.disabledBriefSectionIds,
          settings,
          country,
        });
  return mergeHomeCarouselWithServerHints(userVisibleOrdered, options.serverHints, country);
}

export function setHomeCarouselCardHidden(
  id: HomeCarouselCardId,
  hidden: boolean,
  settings: HomeCarouselSettings,
  country = "US",
): HomeCarouselSettings {
  return saveHomeCarouselSettings(
    { ...settings, hidden: { ...settings.hidden, [id]: hidden } },
    country,
  );
}

export function homeCarouselOrderAfterMove(
  id: HomeCarouselCardId,
  direction: -1 | 1,
  settings: HomeCarouselSettings,
): HomeCarouselCardId[] {
  const order = canonicalHomeCarouselOrder(settings.order);
  if (id === "daily_brief") return order;
  const hasProductivityPair = order.includes("tasks") && order.includes("calendar");
  const groups: HomeCarouselCardId[][] = [];
  for (const cardId of order) {
    if (hasProductivityPair && cardId === "calendar") continue;
    groups.push(hasProductivityPair && cardId === "tasks" ? ["tasks", "calendar"] : [cardId]);
  }
  const groupIndex = groups.findIndex((group) => group.includes(id));
  if (groupIndex < 0) return order;
  const visible = new Set(order.filter((cardId) => settings.hidden[cardId] !== true));
  let nextGroupIndex = groupIndex + direction;
  while (
    nextGroupIndex >= 0 &&
    nextGroupIndex < groups.length &&
    !groups[nextGroupIndex]?.some((cardId) => visible.has(cardId))
  ) {
    nextGroupIndex += direction;
  }
  if (
    nextGroupIndex < 0 ||
    nextGroupIndex >= groups.length ||
    groups[nextGroupIndex]?.includes("daily_brief")
  ) {
    return order;
  }
  [groups[groupIndex], groups[nextGroupIndex]] = [groups[nextGroupIndex], groups[groupIndex]];
  return canonicalHomeCarouselOrder(groups.flat());
}

export function moveHomeCarouselCard(
  id: HomeCarouselCardId,
  direction: -1 | 1,
  settings: HomeCarouselSettings,
  country = "US",
): HomeCarouselSettings {
  const currentOrder = canonicalHomeCarouselOrder(settings.order);
  const nextOrder = homeCarouselOrderAfterMove(id, direction, settings);
  if (nextOrder.every((cardId, index) => cardId === currentOrder[index])) return settings;
  return saveHomeCarouselSettings({ ...settings, order: nextOrder }, country);
}

export function carouselHintsFromHomeApi(homePayload: unknown): string[] {
  const source =
    homePayload && typeof homePayload === "object"
      ? (homePayload as {
          cards?: Array<{ id?: unknown; type?: unknown; payload?: { cardId?: unknown } }>;
        })
      : {};
  const cards = Array.isArray(source.cards) ? source.cards : [];
  return cards
    .map((card) => String(card.id || card.payload?.cardId || card.type || "").trim())
    .filter(Boolean);
}

export function onHomeCarouselSettingsChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(HOME_CAROUSEL_EVENT, listener);
  return () => window.removeEventListener(HOME_CAROUSEL_EVENT, listener);
}
